<template>
    <el-button 
      :type="type" 
      plain 
      :loading="buttonLoading"
      :disabled="buttonDisabled"
      @click.stop="handleClick"
      :class="['icon-button', { 'no-icon': !icon }]"
    >
      <el-icon v-if="icon" class="icon">
        <component :is="icon" />
      </el-icon>
      <span class="text">{{ displayText }}</span>
    </el-button>
  </template>
  
  <script lang="ts" setup>
  import { useI18n } from 'vue-i18n'
import { computed, ref, type PropType } from 'vue'

type ButtonAction = (event: Event) => Promise<unknown> | unknown

const { t } = useI18n()

const props = defineProps({
  icon: {
    type: [String, Object],
    default: null
  },
  text: {
    type: String,
    required: true
  },
  isI18n: {
    type: Boolean,
    default: true
  },
  type: {
    type: String,
    default: 'primary'
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  action: {
    type: Function as PropType<ButtonAction>,
    default: null
  }
})

const emit = defineEmits(['click'])
const internalLoading = ref(false)

const buttonLoading = computed(() => {
  return props.loading || internalLoading.value
})

const buttonDisabled = computed(() => {
  return props.disabled || buttonLoading.value
})

const handleClick = async (event: Event) => {
  event.preventDefault()

  if (buttonDisabled.value) {
    return
  }

  if (!props.action) {
    emit('click', event)
    return
  }

  internalLoading.value = true

  try {
    await props.action(event)
  } finally {
    internalLoading.value = false
  }
}

const displayText = computed(() => {
  return props.isI18n ? t(props.text) : props.text
})
</script>
  <style scoped>
.icon-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    color: #606266;
    border-color: #dcdfe6;
    transition: all 0.3s ease;
  }
  
  .icon-button:hover {
    background-color: rgba(0, 102, 236, 0.1); /* Light blue background on hover */
  }
  
  .icon-button.no-icon {
    padding-left: 16px;
  }
  
  .icon {
    font-size: 14px;
    color: rgba(0, 102, 236, 1);
    transition: color 0.3s ease;
  }
  
  .text {
    font-size: 14px;
    line-height: 1;
    color: rgba(0, 102, 236, 1);
    transition: color 0.3s ease;
  }
  
  .icon-button:hover .icon,
  .icon-button:hover .text {
    color: rgba(0, 102, 236, 0.8); /* Slightly transparent blue on hover */
  }
  
  </style>
